import React, { useMemo } from 'react';
import { Tag, Tooltip } from 'antd';

import { serialize } from '../../utils/kql';
import { removeConditionsAt } from '../../utils/kqlBuilder';
import { summarizeQuery } from '../../utils/filterSummary';
import useQueryLookupTitles from '../../hooks/useQueryLookupTitles';
import { CardOptionLabel } from '../cards/CardVisuals';

const TRUNCATE_AT = 64;

const truncate = text => (text.length > TRUNCATE_AT ? `${text.slice(0, TRUNCATE_AT - 1)}…` : text);

/**
 * The active filter, summarized under a list's toolbar: one dismissible tag
 * per top-level AND condition. A query the builder cannot decompose (nested
 * or/not) gets a single, non-dismissible tag — removing "part" of it would
 * change what it means.
 *
 * Values read as labels, not slugs: a `choice` field's value is looked up in
 * its `options.choices` ("Parent is Person"), member by member for `in`; a
 * `measurement` literal reads as entered ("Weight > 20 lb"), whatever the
 * element's display rules say.
 *
 * A LOOKUP reads as the item, not its id: the ids in the query are batch-
 * resolved (`useQueryLookupTitles`) and labelled through the same resolver the
 * tiles and the read view use, so "resident is 507f…011" is "Resident is Molly
 * Byrne" — and "…" for the moment before the answer arrives.
 *
 * An ELEMENT REFERENCE reads as the other field's LABEL, in the braces the
 * rest of the platform already uses for "the value of that field": "On hand <=
 * {{Reorder at}}", never the raw `{{on-hand}}` slug the query carries. The
 * chip's ✕ removes the whole condition by position, exactly like any other —
 * there is no half of a field-to-field comparison to drop.
 *
 * A PERIOD is one chip, not two. "This week" is two conditions in the query
 * (`>= Sep 8 and < Sep 15`) and one thing the user asked for, so the shared
 * recognizer (`utils/filterSummary` over `utils/datePresets`) folds the pair
 * into "Due: This week" and the chip's ✕ removes both. A pair it does not
 * recognize stays two plain chips, each removable on its own.
 *
 * `hiddenPaths` names root paths whose top-level conditions render nowhere —
 * the toolbar search box already shows `text ~ …`, say. Dismissal still
 * indexes into the FULL condition list, so hiding a chip never shifts which
 * condition its neighbours remove.
 *
 * `readOnly` drops the dismiss affordance from every chip, for surfaces that
 * SUMMARIZE a stored query rather than edit one (a filtered grant, a share
 * link). `className` lets those surfaces name their own container.
 *
 * A CARD condition (`card = …`, `card in (…)` — the "Linked from" door writes
 * one per lending collection) names cards by key or id. Given `resolveCard`
 * (value → card definition, or null), a chip whose every value resolves
 * shows the cards as their badges — a lent card, a parent's, a
 * sub-collection's qualified by the collection it lives in — rather than
 * printing ids.
 */
// The cards a `card` condition names, when every one of them resolves.
function cardsOfCondition(node, resolveCard) {
	// (Summarized conditions are the builder's: `{ path, cmp, value }`, no `op`.)
	if (typeof resolveCard !== 'function' || !node || node.facet) return null;
	if ((node.path || []).length !== 1 || node.path[0] !== 'card' || !['eq', 'in'].includes(node.cmp)) return null;
	const values = node.value?.type === 'list' ? node.value.v : [node.value];
	const cards = (values || []).map(value => (value && typeof value.v === 'string' ? resolveCard(value.v) : null));
	return cards.length && cards.every(Boolean) ? cards : null;
}

export default function FilterChips({
	query = '',
	fields = [],
	onChange,
	readOnly = false,
	className = 'list-filter__chips',
	hiddenPaths = [],
	resolveCard = null,
	// Ids the server said name something removed (X-Query-Warnings).
	removedIds = null,
}) {
	// A lookup condition stores an item id; the chip has to say the item's name.
	const { titleFor } = useQueryLookupTitles(query, fields);
	const { ast, advanced, sentence, entries } = useMemo(
		() => summarizeQuery(query, fields, { resolveValueLabel: (_field, raw) => titleFor(raw), removedIds }),
		[query, fields, titleFor, removedIds]
	);
	const hidden = useMemo(() => new Set(hiddenPaths || []), [hiddenPaths]);

	// An unparseable query never reaches here as chips — the API's 400 clears
	// it (see CollectionItemsPage) — so silence is the right answer.
	if (!ast) return null;

	// A query that is not one chip per condition is one chip: in words when the
	// builder shows it as one level of groups (KPR-978), as its text otherwise.
	if (advanced) {
		const words = sentence || advanced;
		return (
			<div className={className}>
				<Tooltip title={words}>
					<Tag className="list-filter__chip list-filter__chip--advanced">
						{truncate(words)}
					</Tag>
				</Tooltip>
			</div>
		);
	}

	// `hiddenPaths` names ROOT FIELDS (the toolbar's own `text ~ …`); a facet
	// whose card key happens to spell one is a different condition entirely.
	// A search NOT containing a word (`text !~ …`) is not what the box shows
	// (it shows the term that is searched for), so it keeps its chip: a list must
	// never be narrowed by a condition with no mark on screen.
	const isHidden = entry => !entry.facet && hidden.has(entry.path?.[0]) && entry.node?.cmp !== 'ncontains';
	if (entries.every(isHidden)) return null;

	return (
		<div className={className}>
			{entries.map((entry, index) => {
				if (isHidden(entry)) return null;
				const cards = cardsOfCondition(entry.node, resolveCard);
				return (
					<Tag
						className={['list-filter__chip', cards ? 'list-filter__chip--cards' : ''].filter(Boolean).join(' ')}
						key={`${entry.text}-${index}`}
						closable={!readOnly}
						onClose={event => {
							event.preventDefault();
							const next = removeConditionsAt(ast, entry.indices);
							onChange(next ? serialize(next) : undefined);
						}}
					>
						{cards ? (
							<>
								<span className="list-filter__chip-label">{cards.length > 1 ? 'Card is any of' : 'Card is'}</span>
								{cards.map((card, cardIndex) => <CardOptionLabel key={`${card?._id || cardIndex}`} card={card} />)}
							</>
						) : entry.text}
					</Tag>
				);
			})}
		</div>
	);
}
